import {
  MapPin,
  Plus,
  X,
} from 'lucide-react';
import type { DiversSubProject } from '../../hooks/useSchedulerData';
import { getEntityColors } from '../../hooks/useSchedulerData';

interface SubProjectPickerProps {
  projectId: string;
  parentEntity: string;
  subProjects: DiversSubProject[];
  onSelect: (subProject: DiversSubProject) => void;
  onClose: () => void;
  onCreateNew: () => void;
}

export function SubProjectPicker({
  projectId,
  parentEntity,
  subProjects,
  onSelect,
  onClose,
  onCreateNew,
}: SubProjectPickerProps) {
  const filtered = subProjects.filter(sp => sp.parent_project_id === projectId);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexShrink: 0 }}>
          <h2 style={{
            margin: 0, fontSize: '1.25rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px'
          }}>
            <div style={{
              background: getEntityColors(parentEntity).bg,
              padding: '8px',
              borderRadius: '8px'
            }}>
              <MapPin size={20} color={getEntityColors(parentEntity).color} />
            </div>
            Choisir un chantier {parentEntity} Divers
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: '#f1f5f9',
              border: 'none',
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer'
            }}
          >
            <X size={20} color="#64748b" />
          </button>
        </div>

        {/* Liste des sous-chantiers */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {filtered.map(subProj => {
            const entityColor = getEntityColors(parentEntity).color;
            const entityBg = getEntityColors(parentEntity).bg;

            return (
              <button
                key={subProj.id}
                type="button"
                onClick={() => onSelect(subProj)}
                style={{
                  width: '100%',
                  padding: '16px',
                  marginBottom: '8px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  background: 'white',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = entityBg;
                  e.currentTarget.style.borderColor = entityColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }}
              >
                {/* Numéro Divers */}
                <div style={{
                  background: '#f1f5f9',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontFamily: 'monospace',
                  fontWeight: '700',
                  fontSize: '0.95rem',
                  color: entityColor,
                  flexShrink: 0
                }}>
                  {subProj.divers_number}
                </div>

                {/* Infos */}
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '1rem', marginBottom: '2px' }}>
                    {subProj.name}
                  </div>
                  {subProj.address && (
                    <div style={{ fontSize: '0.8rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <MapPin size={12} /> {subProj.address}
                    </div>
                  )}
                </div>

                {/* Zone */}
                {subProj.zone && (
                  <div style={{
                    background: '#f3e8ff',
                    color: '#7e22ce',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    flexShrink: 0
                  }}>
                    Z{subProj.zone}
                  </div>
                )}
              </button>
            );
          })}

          {/* Option : Créer un nouveau */}
          <button
            type="button"
            onClick={onCreateNew}
            style={{
              width: '100%',
              padding: '16px',
              border: '2px dashed #cbd5e1',
              borderRadius: '12px',
              background: '#f8fafc',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              color: '#64748b',
              fontWeight: '600',
              fontSize: '0.9rem',
              marginTop: '8px'
            }}
          >
            <Plus size={18} /> Créer un nouveau chantier {parentEntity} Divers
          </button>
        </div>
      </div>
    </div>
  );
}
